import { formatCount } from "@nexus/ui";
import { WidgetSummary, WidgetFooter, WidgetIssueState } from "@nexus/widget-sdk";
import {
  RankingList,
  RANKING_ROW as ROW,
  RANKING_METRIC as METRIC_COL,
  RANKING_VIZ as VIZ_COL,
  RANKING_META as META_ROW,
} from "@nexus/widget-kit";
import { memo, useId, useMemo } from "react";
import {
  defineLabels,
  firstError,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetState,
  StatusChip,
} from "@nexus/widget-sdk";
import type { StatusState } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import type {
  DetectionPerEngineConfig,
  DetectionPerEngineData,
  DetectionPerEngineEntry,
  DetectionEngineTrendPoint,
} from "./types";

export type {
  DetectionPerEngineConfig,
  DetectionPerEngineData,
  DetectionPerEngineEntry,
  DetectionEngineTrendPoint,
} from "./types";

export const LABELS = defineLabels([
  { key: "emptyTitle", name: "Empty state title", default: "No detections" },
  {
    key: "emptyDescription",
    name: "Empty state description",
    default: "Widen the time range to see engine activity.",
  },
  { key: "summaryLabel", name: "Summary caption", default: "detections" },
  {
    key: "summaryDescription",
    name: "Summary description",
    default: "Ranked by detections",
  },
  { key: "engineHeading", name: "Engine column heading", default: "Engine" },
  {
    key: "detectionsHeading",
    name: "Detections column heading",
    default: "Detections",
  },
  { key: "trendHeading", name: "Trend column heading", default: "Trend" },
  {
    key: "actionableCaption",
    name: "Actionable count caption",
    default: "actionable",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "detection-per-engine",
  name: "Detection per Engine",
  description: "Detections grouped by detection engine.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    engines: {
      source: "detection-per-engine",
      contract: { name: "detection-per-engine", version: 1 },
      configurable: false,
    },
  },
};

export const defaultConfig: DetectionPerEngineConfig = {
  title: "Detection per Engine",
  maxEngines: 10,
};

const EMPTY_ENGINES: DetectionPerEngineEntry[] = [];

// ── Helpers ──────────────────────────────────────────────────────────────────

function actionablePct(total: number, actionable: number): number {
  if (total <= 0) return 0;
  return Math.round(Math.max(0, Math.min(100, (actionable / total) * 100)));
}

// Volume vs baseline mapped onto the shared status scale (same vocabulary as
// Engines Health and response-per-engine): nominal → warn → error.
function getStatus(total: number, expected: number | null): StatusState {
  if (!expected || expected <= 0) return "ok";
  if (total > expected * 1.5) return "error";
  if (total > expected) return "warn";
  return "ok";
}

function getDeltaPct(total: number, expected: number | null): number | null {
  if (!expected || expected <= 0) return null;
  return Math.round(((total - expected) / expected) * 100);
}

function formatSignedPct(value: number): string {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}${Math.abs(value)}%`;
}

// Single tone table: drives both the delta text and the sparkline.
// Nominal stays muted — the row only takes color when something is wrong.
const TONE: Record<StatusState, string> = {
  ok: "text-muted",
  warn: "text-status-warn-text",
  error: "text-status-error-text",
  neutral: "text-muted",
};

// ── Sub-components ────────────────────────────────────────────────────────────

function Separator() {
  return <span className="h-2.5 w-px shrink-0 bg-border/70" />;
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div className={METRIC_COL}>
      <div className="text-xl font-semibold leading-none tracking-display tabular-nums text-primary">
        {value}
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}

function TrendIcon({ up }: { up: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="h-3 w-3 shrink-0"
    >
      {up ? (
        <>
          <path d="M16 7h6v6" />
          <path d="m22 7-8.5 8.5-5-5L2 17" />
        </>
      ) : (
        <>
          <path d="M16 17h6v-6" />
          <path d="m22 17-8.5-8.5-5 5L2 7" />
        </>
      )}
    </svg>
  );
}

function DeltaText({ delta, status }: { delta: number | null; status: StatusState }) {
  if (delta === null) return null;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 font-medium tabular-nums ${TONE[status]}`}
    >
      <TrendIcon up={delta >= 0} />
      {formatSignedPct(delta)}
    </span>
  );
}

const SPARK_W = 96;
const SPARK_H = 32;
const SPARK_PAD_X = 4;
const SPARK_PAD_Y = 5;

function TrendSparkline({ trend }: { trend: DetectionEngineTrendPoint[] }) {
  const gradientId = useId();

  if (trend.length < 2) {
    return <div className="flex h-full items-center justify-end text-2xs text-muted">—</div>;
  }

  const counts = trend.map((point) => point.count);
  const max = Math.max(...counts);
  const min = Math.min(...counts);
  const flat = max === min;
  const range = flat ? 1 : max - min;

  const x = (index: number) =>
    SPARK_PAD_X + (index / (counts.length - 1)) * (SPARK_W - SPARK_PAD_X * 2);

  const y = (count: number) =>
    flat
      ? SPARK_H / 2
      : SPARK_H - SPARK_PAD_Y - ((count - min) / range) * (SPARK_H - SPARK_PAD_Y * 2);

  const line = counts
    .map((count, index) => `${x(index).toFixed(1)},${y(count).toFixed(1)}`)
    .join(" ");

  const area = `${SPARK_PAD_X},${SPARK_H - SPARK_PAD_Y} ${line} ${SPARK_W - SPARK_PAD_X},${SPARK_H - SPARK_PAD_Y}`;

  const lastLeft = ((SPARK_W - SPARK_PAD_X) / SPARK_W) * 100;
  const lastTop = (y(counts[counts.length - 1]) / SPARK_H) * 100;

  return (
    <div className="relative h-full w-full">
      <svg
        viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
        preserveAspectRatio="none"
        className="h-full w-full overflow-visible"
        aria-hidden
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity={0.14} />
            <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
          </linearGradient>
        </defs>
        <polygon points={area} fill={`url(#${gradientId})`} />
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        className="absolute h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current"
        style={{ left: `${lastLeft}%`, top: `${lastTop}%` }}
      />
    </div>
  );
}

const EngineRow = memo(function EngineRow({
  entry,
  actionableCaption,
}: {
  entry: DetectionPerEngineEntry;
  actionableCaption: string;
}) {
  const trend = entry.trend ?? [];
  const expected = entry.expected_count ?? null;

  const pct = actionablePct(entry.total_detections, entry.actionable_count);
  const status = getStatus(entry.total_detections, expected);
  const delta = getDeltaPct(entry.total_detections, expected);

  return (
    <div className={ROW}>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="min-w-0 truncate text-sm font-semibold leading-5 text-primary"
            title={entry.engine_label}
          >
            {entry.engine_label}
          </span>
          {status !== "ok" && <StatusChip status={status} />}
        </div>

        <div className={META_ROW}>
          <DeltaText delta={delta} status={status} />
          {delta !== null && <Separator />}
          <span className="truncate tabular-nums">
            {formatCount(entry.actionable_count)} {actionableCaption} · {pct}%
          </span>
        </div>
      </div>

      <Metric value={formatCount(entry.total_detections)} label="detections" />

      <div className={`${VIZ_COL} ${TONE[status]}`}>
        <TrendSparkline trend={trend} />
      </div>
    </div>
  );
});

function RowSkeleton() {
  return (
    <div className={ROW}>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="h-3 w-32 rounded skeleton" />
        <div className="h-2.5 w-40 rounded skeleton" />
      </div>
      <div className={METRIC_COL}>
        <div className="ml-auto h-5 w-12 rounded skeleton" />
      </div>
      <div className={`${VIZ_COL} rounded skeleton`} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function DetectionPerEngineComponent({ data, config }: WidgetProps<DetectionPerEngineConfig>) {
  const error = firstError(data, ["engines"]);
  const resolved = unwrap<DetectionPerEngineData>(data.engines);
  const isLoading = data.engines?.isLoading ?? false;

  const title = config.title ?? defaultConfig.title ?? "Detection per Engine";
  const labels = resolveLabels(LABELS, config);
  const maxEngines = config.maxEngines ?? defaultConfig.maxEngines ?? 10;

  const allEngines = resolved?.engines ?? EMPTY_ENGINES;

  const { engines, grandTotal, hiddenCount } = useMemo(() => {
    const sorted = [...allEngines].sort((a, b) => b.total_detections - a.total_detections);
    const top = sorted.slice(0, maxEngines);
    const total = allEngines.reduce((sum, e) => sum + e.total_detections, 0);
    return {
      engines: top,
      grandTotal: total,
      hiddenCount: allEngines.length - top.length,
    };
  }, [allEngines, maxEngines]);

  if (error) {
    console.error("[DetectionPerEngineWidget] Failed to load data:", error);
    return (
      <Widget title={title}>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  const hasData = engines.length > 0;

  if (!hasData && isLoading) {
    return (
      <Widget title={title}>
        <div className="h-full overflow-hidden">
          {Array.from({ length: Math.min(maxEngines, 5) }, (_, index) => (
            <RowSkeleton key={index} />
          ))}
        </div>
      </Widget>
    );
  }

  if (!hasData) {
    return (
      <Widget title={title}>
        <WidgetState title={labels.emptyTitle} description={labels.emptyDescription} />
      </Widget>
    );
  }

  return (
    <Widget title={title} actions={isLoading ? <UpdatingPill /> : undefined}>
      <div className="@container flex h-full min-h-0 flex-col">
        <WidgetSummary
          value={formatCount(grandTotal)}
          exactValue={grandTotal.toLocaleString()}
          label={labels.summaryLabel}
          description={labels.summaryDescription}
        />
        <RankingList
          identity={labels.engineHeading}
          metric={labels.detectionsHeading}
          visualization={labels.trendHeading}
        >
          {engines.map((entry) => (
            <EngineRow
              key={entry.engine_id}
              entry={entry}
              actionableCaption={labels.actionableCaption}
            />
          ))}
        </RankingList>
        <WidgetFooter>
          <span>
            {engines.length} of {allEngines.length} engines
          </span>
          {hiddenCount > 0 ? <span>· {hiddenCount} not shown</span> : null}
        </WidgetFooter>
      </div>
    </Widget>
  );
}

export const Component = DetectionPerEngineComponent;

const widgetModule: WidgetModule<DetectionPerEngineConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
